import 'utils/redirectFromHttpToHttps';
import 'utils/idempotentBabelPolyfill';
/**
 * Splash page
 * ===========
 *
 * A home for everything we inject on the CMS.
 */
import { getInstance as getBootstrap } from 'utils/bootstrap';
import { getInstance as getFlags } from 'utils/flags';
import { constant as legacyConstant } from 'utils/legacyBootstrap';
import exposer from 'utils/exposer';
import SplashApiClient from 'lib/SplashApiClient';
import AttendeeApiClient from 'lib/SplashApiClient/subclients/AttendeeApiClient';
import SessionApiClient from 'lib/SplashApiClient/subclients/SessionApiClient';
import FormRendererV2ApiClient from 'lib/SplashApiClient/subclients/FormRendererV2ApiClient';
import get from 'lodash/get';

import { combineModulesReducer, APP } from 'common/store';
import { setGuestAccessHash, setCustomFormValues } from 'common/compounds/FormRenderer/actions';
import { addTicketTypeData } from 'common/entities/ticketType';
import ticketTypeModule, { setTicketTypeId } from 'common/store/ticketType';
import { REGISTRATION_METHOD_TICKETS, REGISTRATION_METHOD_RSVP } from 'common/constants/registrationMethods';

import createFormRendererInterface from 'common/compounds/FormRenderer/interface';
import { initialEntities as formRendererEntities } from 'common/compounds/FormRenderer/reducers';
import {
    reducer as notificationReducer,
    notificationNameSpace,
    notifyError,
    notifyInfo,
    notifySuccess,
} from 'common/compounds/Notification';
import { QUERY_PARAM_FORM_PREVIEW_ID } from 'utils/resolveUrl';

import tuningForkIframeLoader from 'lib/tuning-fork/parent-iframe/loader';

import createReactApp from './createReactApp';
import createFormRendererView from './createFormRendererView';
import createConfirmationsApp from './createConfirmationsApp';
import createGuestFooterApp from './createGuestFooterApp';
import getComponentInterfaceManagerAndStore from './getComponentInterfaceManagerAndStore';
import loadForm, { isAutoMountingReactApp, loadUpdateResponses } from './loadForm';
import splashPageReducer, {
    REDUCER_NAMESPACE as SPLASH_PAGE_REDUCER_NAMESPACE,
    showUnpublishedNotification,
} from './modules/splashPage';
import splashPageEntitiesReducer from './modules/entities';
import mobileRsvpLoader from './mobile-rsvp';
import './styles.styl';

/**
 * Get the bootstrap data and flags that PHP left for us
 */
const bootstrap = getBootstrap();
const flags = getFlags();

exposer({
    'SPLASH.pageData': (path, fallback) => bootstrap.get(path ? `splashPage.${path}` : 'splashPage', fallback),
    'SPLASH.flag': (name) => flags.bool(name),
    'SPLASH.legacyConstant': legacyConstant,
});

const baseApiUrl = bootstrap.get('app.splashApiUrl', false);

if (baseApiUrl) {
    // Create the ApiClient that is utilizes a user authenticated access token
    const apiClientOpts = { baseUrl: baseApiUrl };
    if (bootstrap.get('splashPage.accessToken', false)) {
        apiClientOpts.accessToken = bootstrap.get('splashPage.accessToken');
    }
    const apiClient = new SplashApiClient(apiClientOpts);

    // Create an API client for use with the attendee authenticated endpoints. This needs to be
    // separate because it uses a different access token.
    const apiClientForAttendee = new SplashApiClient({ baseUrl: baseApiUrl });

    exposer({
        'SPLASH.apiClient': apiClient,
        'SPLASH.sessionApiClient': new SessionApiClient(apiClient),
        'SPLASH.formRendererV2ApiClient': new FormRendererV2ApiClient(apiClient),
        // It is important to note that this client does NOT have a magic link on instantiation.
        // That will need to be provided by calling SPLASH.attendeeApiClient.setGuestAccessHash()
        'SPLASH.attendeeApiClient': new AttendeeApiClient(apiClientForAttendee),
    });
}

const isTicketedEvent = () => get(window, '_event.rsvp_method') === REGISTRATION_METHOD_TICKETS;
const isRsvpEvent = () => get(window, '_event.rsvp_method') === REGISTRATION_METHOD_RSVP;
const modifyRegistration = bootstrap.get('splashPage.modifyRegistration', false);

const shouldLoadFormRendererV3 = () =>
    // only load if guests can submit registrations or we're updating the response
    (['open', 'waitlist'].includes(get(window, '_event.rsvp_status')) || modifyRegistration) &&
    // If ticketed and has flag on.
    ((isTicketedEvent() && getFlags().bool('useTicketSelectionFormRendererInTicketSplashPage')) ||
        // If RSVP and has flag on.
        (isRsvpEvent() && getFlags().bool('useFormRendererV3InRsvpSplashPage')));

/**
 * Execute functionality that depends on outer frame having loaded all JS code.
 * Any interactions with the store or component interface manager for example need to be here.
 */
function initApp() {
    const insideEditingIframe = bootstrap.get('splashPage.insideEditingIframe');
    const { store, componentInterfaceManager } = getComponentInterfaceManagerAndStore(insideEditingIframe);
    const urlParams = new URLSearchParams(window.location.search);
    const formPreviewId = urlParams.get(QUERY_PARAM_FORM_PREVIEW_ID);

    // Register the splash page namespace reducer.
    componentInterfaceManager.register(() => ({
        reducerNamespace: SPLASH_PAGE_REDUCER_NAMESPACE,
        reducer: splashPageReducer,
    }));

    // Avoid registering reducers that would be loaded by the parent frame, workspace,
    // when in edit mode.
    if (!insideEditingIframe) {
        componentInterfaceManager.register(
            () => ({
                reducerNamespace: notificationNameSpace,
                reducer: notificationReducer,
            }),
            notificationNameSpace,
        );
        componentInterfaceManager.register(createFormRendererInterface, 'FormRenderer');

        // Register entity reducer for form renderer entities.
        componentInterfaceManager.register(
            () => ({
                entityKeys: Object.keys(formRendererEntities),
                defaultEntities: formRendererEntities,
                entityReducer: splashPageEntitiesReducer,
            }),
            'formRendererEntities',
        );

        componentInterfaceManager.registerNamespace(APP, combineModulesReducer([ticketTypeModule]));
    }

    const formRequest = shouldLoadFormRendererV3() ? loadForm(store, formPreviewId) : null;

    const reactApp = createReactApp(store);

    const isUnpublished = bootstrap.get('splashPage.isUnpublished', false);
    const isEventPageInEditMode = bootstrap.get('splashPage.isEventPageInEditMode', false);

    if (isUnpublished && !isEventPageInEditMode) {
        store.dispatch(showUnpublishedNotification());
    }

    if (get(window, '_event.rsvp_method') === REGISTRATION_METHOD_TICKETS) {
        const ticketsData = get(window, '_event.tickets', []);
        ticketsData.forEach((ticketData) => {
            store.dispatch(addTicketTypeData(ticketData));
            store.dispatch(setTicketTypeId(`${ticketData.ticketId}`));
        });
    }

    exposer({
        'SPLASH.formRequest': formRequest,
        'SPLASH.showFormRenderer': () => {
            reactApp.showFormRenderer();
            return reactApp;
        },
        'SPLASH.insertFormRenderer': (element) => createFormRendererView(store, element),
        'SPLASH.loadUpdateResponses': () => loadUpdateResponses(store),
        'SPLASH.setGuestAccessHash': (val) => store.dispatch(setGuestAccessHash(val)),
        'SPLASH.notifyInfo': (...args) => store.dispatch(notifyInfo(...args)),
        'SPLASH.notifyError': (...args) => store.dispatch(notifyError(...args)),
        'SPLASH.notifySuccess': (...args) => store.dispatch(notifySuccess(...args)),
        'SPLASH.setCustomFormValues': (formValues) => store.dispatch(setCustomFormValues(formValues)),
        'SPLASH.renderConfirmationModal': ({ serverResponse, triggerTypes }) =>
            createConfirmationsApp().renderConfirmationsModal({ serverResponse, triggerTypes }),
    });

    const onPageConfirmationsTrigger = bootstrap.get('onPageConfirmationsTrigger');

    if (
        onPageConfirmationsTrigger &&
        typeof onPageConfirmationsTrigger === 'object' &&
        onPageConfirmationsTrigger.serverResponse &&
        Array.isArray(onPageConfirmationsTrigger.triggerTypes)
    ) {
        createConfirmationsApp().renderConfirmationsModal({
            serverResponse: onPageConfirmationsTrigger.serverResponse,
            triggerTypes: onPageConfirmationsTrigger.triggerTypes,
        });
    }

    if (isAutoMountingReactApp) {
        createReactApp(store).showFormRenderer();
    }
    createGuestFooterApp();

    tuningForkIframeLoader();
    mobileRsvpLoader();

    // Expose some objects for debugging
    if (__DEVELOPMENT__) {
        window.store = store;
    }
}
exposer({ 'SPLASH.initializeSplashPageWebpackApp': initApp });

// Expose some objects for debugging
if (__DEVELOPMENT__) {
    window.bootstrap = bootstrap;
    window.flags = flags;
}
